import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, RefreshCw, Search, Star, ExternalLink, X } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';

const API = 'https://api.glowytics.com/api/app-radar';

/* ── Helpers ───────────────────────────────────────────────────────── */

const formatNumber = (n) => {
  if (n === null || n === undefined) return '-';
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return Number(n).toLocaleString('en-US');
};
const formatDate = (s) => {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatShortDate = (s) => {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};
const rating = (r) => (r == null ? '-' : Number(r).toFixed(2));

const COUNTRIES = [
  { code: 'tr', label: 'Türkiye' },
  { code: 'us', label: 'United States' },
  { code: 'gb', label: 'United Kingdom' },
  { code: 'de', label: 'Germany' },
];

const AppStoreLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-label="App Store" role="img">
    <defs>
      <linearGradient id="appstore-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#18BFFB" />
        <stop offset="1" stopColor="#2072F3" />
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="5.5" fill="url(#appstore-grad)" />
    <path d="M12.6 6.2 7.3 15.4M11.4 6.2l5.3 9.2M6 13.2h7.6M15.3 13.2H18M7.3 15.4l-.9 1.6M16.7 15.4l.9 1.6"
      stroke="#fff" strokeWidth="1.7" strokeLinecap="round" fill="none" />
  </svg>
);

const GooglePlayLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Google Play" role="img">
    <g strokeLinejoin="round" strokeWidth="0.6">
      <path d="M3.5 2 13.6 12 3.5 22Z" fill="#00A0FF" stroke="#00A0FF" />
      <path d="M3.5 2 17.1 8.7 13.6 12Z" fill="#00D26A" stroke="#00D26A" />
      <path d="M3.5 22 13.6 12 17.1 15.3Z" fill="#FF3A44" stroke="#FF3A44" />
      <path d="M17.1 8.7 20.6 10.5Q22 12 20.6 13.5L17.1 15.3 13.6 12Z" fill="#FFC400" stroke="#FFC400" />
    </g>
  </svg>
);

const StoreBadge = ({ store, size = 16 }) => (
  <span className="inline-flex shrink-0" title={store === 'apple' ? 'App Store' : 'Google Play'}>
    {store === 'apple' ? <AppStoreLogo size={size} /> : <GooglePlayLogo size={size} />}
  </span>
);

const Stars = ({ value, size = 12 }) => (
  <span className="inline-flex">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={size} className={i <= Math.round(value || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} />
    ))}
  </span>
);

const Delta = ({ now, before, digits = 2, suffix = '' }) => {
  if (now == null || before == null) return <span className="text-slate-400">–</span>;
  const d = Number(now) - Number(before);
  if (Math.abs(d) < Math.pow(10, -digits) / 2) return <span className="text-slate-400">0{suffix}</span>;
  const cls = d > 0 ? 'text-emerald-600' : 'text-red-600';
  const txt = digits === 0 ? formatNumber(Math.abs(d)) : Math.abs(d).toFixed(digits);
  return <span className={cls}>{d > 0 ? '+' : '−'}{txt}{suffix}</span>;
};

/* ── Page ──────────────────────────────────────────────────────────── */

export default function AppRadar() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';
  const headers = useMemo(() => ({ 'X-Brand-ID': brandId }), [brandId]);

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ekleme
  const [store, setStore] = useState('google');
  const [country, setCountry] = useState('tr');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const selected = apps.find(a => a.id === selectedId) || null;

  const loadApps = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/apps`, { headers, credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to load apps');
      setApps(j.apps || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [brandId, headers]);

  useEffect(() => { loadApps(); }, [loadApps]);

  const isLink = /^(https?:\/\/|apps\.apple\.com|play\.google\.com)/i.test(term.trim()) || /^(id)?\d{5,}$/i.test(term.trim()) || /^[a-z][\w]*(\.[\w]+)+$/i.test(term.trim());

  const doSearch = async (e) => {
    e?.preventDefault();
    const t = term.trim();
    if (!t) return;
    if (isLink) return addApp(t);
    setSearching(true); setError(''); setResults([]);
    try {
      const r = await fetch(`${API}/search?store=${store}&country=${country}&term=${encodeURIComponent(t)}`, { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Search failed');
      setResults(j.results || []);
      if (!(j.results || []).length) setError('No apps found.');
    } catch (err) { setError(err.message); }
    finally { setSearching(false); }
  };

  const addApp = async (input, storeOverride) => {
    setAdding(input); setError('');
    try {
      const r = await fetch(`${API}/app/add`, {
        method: 'POST', credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ input, store: storeOverride || store, country }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to add app');
      setResults([]); setTerm('');
      await loadApps();
      setSelectedId(j.appId);
    } catch (err) { setError(err.message); }
    finally { setAdding(''); }
  };

  const removeApp = async (app) => {
    if (!window.confirm(`Remove "${app.title}" from App Radar?`)) return;
    await fetch(`${API}/app/${app.id}`, { method: 'DELETE', headers, credentials: 'include' });
    if (selectedId === app.id) setSelectedId(null);
    loadApps();
  };

  if (!brandId) {
    return <div className="p-8 text-slate-500">Select a brand to use App Radar.</div>;
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">App Radar</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track App Store and Google Play apps. Rating, rating count, installs and reviews are collected every night.
        </p>
      </div>

      {/* Add */}
      <form onSubmit={doSearch} className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
            {['google', 'apple'].map(s => (
              <button key={s} type="button" onClick={() => { setStore(s); setResults([]); }}
                className={`px-3 py-2 ${store === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                <span className="inline-flex items-center gap-1.5"><StoreBadge store={s === 'google' ? 'google' : 'apple'} />{s === 'google' ? 'Google Play' : 'App Store'}</span>
              </button>
            ))}
          </div>
          <select value={country} onChange={e => setCountry(e.target.value)}
            className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          <div className="flex-1 min-w-[260px] relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={term} onChange={e => setTerm(e.target.value)}
              placeholder="Search by app name, or paste a store link / package name"
              className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
          <button type="submit" disabled={searching || !!adding || !term.trim()}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
            {adding ? 'Adding…' : searching ? 'Searching…' : isLink ? 'Add app' : 'Search'}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        {results.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {results.map(r => (
              <div key={r.storeAppId} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                <img src={r.iconUrl} alt="" className="w-10 h-10 rounded-lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 truncate">{r.developer}</div>
                  {r.rating != null && <div className="text-xs text-slate-500">★ {rating(r.rating)}{r.ratingCount ? ` · ${formatNumber(r.ratingCount)}` : ''}</div>}
                </div>
                <button type="button" onClick={() => addApp(r.storeAppId, r.store)} disabled={!!adding}
                  className="px-3 py-1.5 rounded-md border border-indigo-200 text-indigo-700 text-xs font-medium hover:bg-indigo-50 disabled:opacity-50">
                  {adding === r.storeAppId ? 'Adding…' : 'Track'}
                </button>
              </div>
            ))}
          </div>
        )}
      </form>

      {/* Apps */}
      {loading && !apps.length ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : !apps.length ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500 text-sm">
          No apps tracked yet. Search for an app above to start collecting its data.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">App</th>
                <th className="text-right px-3 py-3">Rating</th>
                <th className="text-right px-3 py-3">Δ 7d</th>
                <th className="text-right px-3 py-3">Ratings</th>
                <th className="text-right px-3 py-3">New ratings 7d</th>
                <th className="text-right px-3 py-3">Reviews</th>
                <th className="text-right px-3 py-3">Installs</th>
                <th className="text-right px-3 py-3">Reviews 30d</th>
                <th className="text-right px-3 py-3">Avg 30d</th>
                <th className="text-right px-3 py-3">1–2★ 30d</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {apps.map(a => (
                <tr key={a.id} onClick={() => setSelectedId(a.id)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedId === a.id ? 'bg-indigo-50/60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={a.icon_url} alt="" className="w-9 h-9 rounded-lg" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate max-w-[240px]">{a.title}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <StoreBadge store={a.store} />
                          <span className="text-[10px] text-slate-400 uppercase">{a.country}</span>
                          {a.last_error && <span className="text-[10px] text-red-500" title={a.last_error}>sync error</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right px-3 py-3 font-semibold text-slate-900">{rating(a.rating)}</td>
                  <td className="text-right px-3 py-3"><Delta now={a.rating} before={a.rating_7d} /></td>
                  <td className="text-right px-3 py-3">{formatNumber(a.rating_count)}</td>
                  <td className="text-right px-3 py-3"><Delta now={a.rating_count} before={a.rating_count_7d} digits={0} /></td>
                  <td className="text-right px-3 py-3">{a.store === 'apple' ? <span className="text-slate-400" title="App Store does not publish a written review count">n/a</span> : formatNumber(a.review_count)}</td>
                  <td className="text-right px-3 py-3">{a.store === 'apple' ? <span className="text-slate-400" title="App Store does not publish installs">n/a</span> : (a.installs_text || formatNumber(a.installs))}</td>
                  <td className="text-right px-3 py-3">{formatNumber(a.reviews_30d)}</td>
                  <td className="text-right px-3 py-3">{a.avg_score_30d != null ? a.avg_score_30d.toFixed(2) : '-'}</td>
                  <td className="text-right px-3 py-3">
                    {a.reviews_30d ? <span className={a.negative_30d / a.reviews_30d > 0.3 ? 'text-red-600 font-medium' : ''}>
                      {a.negative_30d} <span className="text-slate-400">({Math.round(a.negative_30d / a.reviews_30d * 100)}%)</span>
                    </span> : '-'}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); removeApp(a); }}
                      className="text-slate-400 hover:text-red-600" title="Remove"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">
        "Reviews 30d" and the 1–2★ share are computed from reviews collected by App Radar. App Store review feeds only expose the latest ~500 reviews per country.
      </p>

      {selected && (
        <AppDetail key={selected.id} app={selected} headers={headers} onClose={() => setSelectedId(null)} onRefreshed={loadApps} />
      )}
    </div>
  );
}

/* ── Detail ────────────────────────────────────────────────────────── */

function AppDetail({ app, headers, onClose, onRefreshed }) {
  const [stats, setStats] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [scoreFilter, setScoreFilter] = useState(0);
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const PAGE = 30;

  useEffect(() => {
    fetch(`${API}/app/${app.id}/daily-stats`, { headers, credentials: 'include' })
      .then(r => r.json()).then(j => { setStats(j.stats || []); setWeekly((j.weekly || []).slice(-26)); })
      .catch(() => {});
  }, [app.id, headers, app.last_synced_at]);

  useEffect(() => {
    const p = new URLSearchParams({ limit: PAGE, offset });
    if (scoreFilter) p.set('score', scoreFilter);
    if (qApplied) p.set('q', qApplied);
    fetch(`${API}/app/${app.id}/reviews?${p}`, { headers, credentials: 'include' })
      .then(r => r.json()).then(j => { setReviews(j.reviews || []); setTotal(j.total || 0); })
      .catch(() => {});
  }, [app.id, headers, scoreFilter, qApplied, offset, app.last_synced_at]);

  const refresh = async () => {
    setRefreshing(true);
    try { await fetch(`${API}/app/${app.id}/refresh`, { method: 'POST', headers, credentials: 'include' }); await onRefreshed(); }
    finally { setRefreshing(false); }
  };

  const histogram = app.histogram
    ? [5, 4, 3, 2, 1].map(s => ({ star: `${s}★`, count: Number(app.histogram[s] || 0) }))
    : null;
  const histTotal = histogram ? histogram.reduce((s, h) => s + h.count, 0) : 0;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start gap-4">
        <img src={app.icon_url} alt="" className="w-16 h-16 rounded-2xl" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-slate-900">{app.title}</h2>
            <StoreBadge store={app.store} size={20} />
            {app.store_url && (
              <a href={app.store_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600"><ExternalLink size={14} /></a>
            )}
          </div>
          <div className="text-sm text-slate-500">{app.developer}{app.genre ? ` · ${app.genre}` : ''}</div>
          <div className="text-xs text-slate-400 mt-1">
            {app.version ? `v${app.version} · ` : ''}Last release {formatDate(app.last_release_at)} · Synced {formatDate(app.last_synced_at)}
          </div>
        </div>
        <button onClick={refresh} disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <Kpi label="Rating" value={rating(app.rating)} sub={<Stars value={app.rating} />} />
        <Kpi label="Ratings" value={formatNumber(app.rating_count)} sub={<>7d <Delta now={app.rating_count} before={app.rating_count_7d} digits={0} /> · 30d <Delta now={app.rating_count} before={app.rating_count_30d} digits={0} /></>} />
        <Kpi label="Written reviews" value={app.store === 'apple' ? 'n/a' : formatNumber(app.review_count)} sub={`${formatNumber(app.stored_reviews)} collected`} />
        <Kpi label="Installs" value={app.store === 'apple' ? 'n/a' : (app.installs_text || formatNumber(app.installs))} sub={app.store === 'apple' ? 'Not published by App Store' : ''} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2">Rating distribution</h3>
          {histogram ? (
            <div className="space-y-1.5">
              {histogram.map(h => (
                <div key={h.star} className="flex items-center gap-2 text-xs">
                  <span className="w-6 text-slate-500">{h.star}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded">
                    <div className="h-2.5 rounded bg-amber-400" style={{ width: `${histTotal ? (h.count / histTotal) * 100 : 0}%` }} />
                  </div>
                  <span className="w-14 text-right text-slate-500">{histTotal ? Math.round(h.count / histTotal * 100) : 0}%</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-400">App Store does not publish the rating distribution.</div>
          )}
        </div>
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2">Rating count (daily)</h3>
          {stats.length < 2 ? (
            <div className="text-xs text-slate-400">Trend builds up as nightly snapshots are collected.</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={stats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 10 }} width={45} domain={['auto', 'auto']} />
                <Tooltip labelFormatter={formatDate} formatter={(v) => formatNumber(v)} />
                <Line type="monotone" dataKey="rating_count" name="Ratings" stroke="#6366f1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2">Weekly reviews (collected)</h3>
          {weekly.length === 0 ? (
            <div className="text-xs text-slate-400">No reviews collected yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="week" tickFormatter={formatShortDate} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                <Tooltip labelFormatter={(w) => `Week of ${formatDate(w)}`}
                  formatter={(v, n, p) => n === 'Reviews' ? [`${v} (avg ${p.payload.avg_score?.toFixed(2)}★)`, n] : [v, n]} />
                <Bar dataKey="reviews" name="Reviews" fill="#c7d2fe" />
                <Bar dataKey="negative" name="1–2★" fill="#f87171" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="text-sm font-medium text-slate-700 mr-2">Reviews <span className="text-slate-400 font-normal">({formatNumber(total)})</span></h3>
          {[0, 5, 4, 3, 2, 1].map(s => (
            <button key={s} onClick={() => { setScoreFilter(s); setOffset(0); }}
              className={`px-2.5 py-1 rounded-full text-xs border ${scoreFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {s === 0 ? 'All' : `${s}★`}
            </button>
          ))}
          <form onSubmit={(e) => { e.preventDefault(); setQApplied(q.trim()); setOffset(0); }} className="ml-auto">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search in reviews"
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-56" />
          </form>
        </div>
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
          {reviews.length === 0 && <div className="p-4 text-sm text-slate-400">No reviews.</div>}
          {reviews.map(r => (
            <div key={r.review_id} className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Stars value={r.score} />
                <span className="font-medium text-slate-700">{r.user_name || 'Anonymous'}</span>
                <span>· {formatDate(r.reviewed_at)}</span>
                {r.version && <span>· v{r.version}</span>}
                {r.thumbs_up > 0 && <span>· 👍 {r.thumbs_up}</span>}
              </div>
              {r.title && <div className="text-sm font-medium text-slate-900 mt-1">{r.title}</div>}
              <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{r.text}</div>
              {r.reply_text && (
                <div className="mt-2 ml-4 pl-3 border-l-2 border-indigo-200 text-xs text-slate-600">
                  <span className="font-medium text-slate-700">Developer reply</span> · {formatDate(r.reply_at)}
                  <div className="mt-0.5 whitespace-pre-line">{r.reply_text}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        {total > PAGE && (
          <div className="flex items-center justify-end gap-2 mt-3 text-sm">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
              className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Previous</button>
            <span className="text-slate-500">{offset + 1}–{Math.min(offset + PAGE, total)} of {formatNumber(total)}</span>
            <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}
              className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Kpi = ({ label, value, sub }) => (
  <div className="border border-slate-200 rounded-lg p-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-xl font-semibold text-slate-900 mt-0.5">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export { StoreBadge };
